import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IAdminMovePersonResult } from './admin-people.service';

export interface ISelectedPerson {
  org_person_id: number;
  first_name: string;
  last_name: string;
}

// The single owner of move state (TK-077): held so it outlives any one
// component instance. The panel and the list both read and write here rather
// than holding move state of their own. Three transitions are kept distinct:
// dismiss (close, keep everything), discard (operator-confirmed, clears the
// gathering) and a completed move (clears the gathering, replaces it with
// results).
@Injectable({ providedIn: 'root' })
export class AdminMoveSelectionService {
  open = false;
  gathered = new Map<number, ISelectedPerson>();
  destinationOrgId: number | null = null;
  destinationOrgName = '';
  results: IAdminMovePersonResult[] | null = null;
  moved$ = new Subject<void>();
  // Emits whenever `open` changes — the panel and the list are separate
  // branches of the component tree (the panel is projected into the shell,
  // the list sits behind the router outlet), so an OnPush list needs this to
  // know when to re-check its move-mode-dependent rendering.
  openChanged$ = new Subject<boolean>();
  // Emits whenever the gathering itself changes (add/remove/discard/complete)
  // — the panel and the list are separate OnPush branches of the component
  // tree with no shared ancestor, so a tick made from the list side must
  // explicitly notify the panel to re-check its count/list/confirm-button
  // rendering.
  gatheringChanged$ = new Subject<void>();

  get selectedPeople(): ISelectedPerson[] {
    return Array.from(this.gathered.values());
  }

  get selectionCount(): number {
    return this.gathered.size;
  }

  has(orgPersonId: number): boolean {
    return this.gathered.has(orgPersonId);
  }

  add(person: ISelectedPerson): void {
    this._clearCompletedMove();
    this.gathered.set(person.org_person_id, person);
    this.gatheringChanged$.next();
  }

  remove(orgPersonId: number): void {
    this.gathered.delete(orgPersonId);
    this.gatheringChanged$.next();
  }

  openPanel(): void {
    this._clearCompletedMove();
    this.open = true;
    this.openChanged$.next(this.open);
  }

  // Dismiss closes the panel without clearing anything (AC4) — reopening
  // must show the same gathering, destination and results.
  dismiss(): void {
    this.open = false;
    this.openChanged$.next(this.open);
  }

  // Discard is the operator's explicit, confirmation-gated decision to empty
  // the gathering — distinct from dismiss.
  discard(): void {
    this.gathered.clear();
    this.results = null;
    this.gatheringChanged$.next();
  }

  setDestination(id: number, name: string): void {
    this.destinationOrgId = id;
    this.destinationOrgName = name;
  }

  clearDestination(): void {
    this.destinationOrgId = null;
  }

  completeMove(results: IAdminMovePersonResult[]): void {
    this.gathered.clear();
    this.results = results;
    this.destinationOrgId = null;
    this.destinationOrgName = '';
    this.moved$.next();
    this.gatheringChanged$.next();
  }

  // A completed move's results stay visible until the next move begins (first
  // tick or the Move people trigger). Emits only when there was something to
  // clear, so the panel re-renders without extra events otherwise.
  private _clearCompletedMove(): void {
    if (this.results === null) {
      return;
    }
    this.results = null;
    this.gatheringChanged$.next();
  }
}
